import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { absenceReasonLabel } from './AbsenceModal';
import {
  MonthlyDeclaration,
  EligibleEmployee,
  ExcludedEmployee,
  EligibleEmployeesResponse,
} from '../../../models/monthly-declaration.model';
import { Payslip } from '../../../models/payroll-run.model';
import { DeclarationDA } from '../../../services/admin/declaration.service';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { formatMoney } from '../../../scripts/format.value.script';
import AppPreloader from '../../../components/Preloader';

interface DeclarationRow {
  employee: EligibleEmployee;
  declaration: MonthlyDeclaration | null;
  payslip: Payslip | null;
}

interface MonthlyDeclarationTableProps {
  companyId: string;
  runId: string;
  year: number;
  month: number;
  lang: any;
  editable: boolean;
  payslips: Payslip[] | null;
  onViewPayslip?: (payslip: Payslip) => void;
  pdfLoading?: string | null;
}

function exclusionLabel(lang: any, reason: string): string {
  const map: Record<string, string> = {
    status_draft: lang.labels.exclusionStatusDraft,
    status_terminated: lang.labels.exclusionStatusTerminated,
    no_contract: lang.labels.exclusionNoContract,
    hire_after_period: lang.labels.exclusionHireAfterPeriod,
    contract_ended_before_period: lang.labels.exclusionContractEndedBeforePeriod,
  };
  return map[reason] ?? reason;
}

function payslipEmployeeName(payslip: Payslip): string {
  const snap = payslip.employee_snapshot;
  if (!snap) return '-';
  const cs = snap.contact_snapshot ?? snap;
  return `${cs.first_name ?? ''} ${cs.last_name ?? ''}`.trim() || snap.employee_number || '-';
}

function payslipGross(payslip: Payslip): number | null {
  return payslip.calculation_result?.gross ?? payslip.calculation_result?.totals?.gross ?? null;
}

function payslipNet(payslip: Payslip): number | null {
  return payslip.calculation_result?.net ?? payslip.calculation_result?.totals?.net ?? null;
}

const MonthlyDeclarationTable: React.FunctionComponent<MonthlyDeclarationTableProps> = ({
  companyId, runId, year, month, lang, editable, payslips, onViewPayslip, pdfLoading,
}) => {
  const { lang: langState } = StateLanguage();
  const langCode = langState.code;

  const [rows, setRows] = useState<DeclarationRow[] | null>(null);
  const [excluded, setExcluded] = useState<ExcludedEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [eligibleRes, declRes] = await Promise.all([
        DeclarationDA.getEligibleEmployees(companyId, runId),
        DeclarationDA.getDeclarations(companyId, year, month),
      ]);

      const eligibleData: EligibleEmployeesResponse = eligibleRes.status === 200
        ? eligibleRes.data
        : { eligible: [], excluded: [] };

      const declarations: MonthlyDeclaration[] = declRes.status === 200
        ? (Array.isArray(declRes.data) ? declRes.data : declRes.data?.items ?? [])
        : [];

      const declMap = new Map<string, MonthlyDeclaration>();
      for (const d of declarations) {
        declMap.set(d.id_employee, d);
      }

      // Build payslip map by employee id
      const payslipMap = new Map<string, Payslip>();
      if (payslips) {
        for (const p of payslips) {
          if (p.id_employee) payslipMap.set(p.id_employee, p);
        }
      }

      const eligible = eligibleData.eligible ?? [];
      setExcluded(eligibleData.excluded ?? []);

      const newRows: DeclarationRow[] = eligible.map(emp => ({
        employee: emp,
        declaration: declMap.get(emp.id) ?? null,
        payslip: payslipMap.get(emp.id) ?? null,
      }));

      setRows(newRows);
    } catch (err) {
      console.error('[MonthlyDeclarationTable] Failed to load data:', err);
      setRows([]);
      setExcluded([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, runId, year, month, payslips]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Loading state
  if (loading || rows === null) return <AppPreloader />;

  // Load error state
  if (loadError && rows.length === 0) {
    return (
      <div className="card mb-5">
        <div className="card-body text-center py-10">
          <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3 d-block"></i>
          <p className="text-muted fw-bold fs-6 mb-3">{lang.labels.loadError}</p>
          <button className="btn btn-sm btn-primary" onClick={loadData}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            {lang.labels.retry}
          </button>
        </div>
      </div>
    );
  }

  const isHourly = (row: DeclarationRow) => row.employee.contract?.salary_mode === 'hourly';

  // Determine whether to show AS days and absence days columns
  const showAsDays = rows.some(row => {
    const asDays = row.declaration?.as_days ?? row.employee.periodDays?.full ?? 30;
    return asDays !== 30;
  });
  const showAbsenceDays = rows.some(row => {
    const absenceDays = row.declaration?.absence_days ?? 0;
    return absenceDays > 0;
  });

  // Check if any payslips exist
  const hasPayslips = rows.some(row => row.payslip != null);

  return (
    <div className="card mb-5">
      <div className="card-header">
        <div className="card-title">
          <h4 className="fw-bolder">{lang.labels.monthlyDeclaration}</h4>
        </div>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="text-muted fw-bold text-center py-5">{lang.labels.noEligibleEmployees}</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed mb-2">
              <thead className="border-bottom">
                <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                  <th className="min-w-150px">{lang.labels.name}</th>
                  <th className="min-w-80px">{lang.labels.salaryMode}</th>
                  {showAsDays && <th className="min-w-80px">{lang.labels.socialInsuranceDays}</th>}
                  {showAbsenceDays && <th className="min-w-80px">{lang.labels.absenceDays}</th>}
                  <th className="min-w-80px">{lang.labels.workingHours}</th>
                  {hasPayslips && <th className="min-w-100px">{lang.labels.grossTotal}</th>}
                  {hasPayslips && <th className="min-w-100px">{lang.labels.netTotal}</th>}
                  <th className="min-w-80px">{lang.labels.status}</th>
                  <th className="w-100px text-end">{lang.labels.actions}</th>
                </tr>
              </thead>
              <tbody className="text-secondary fw-bold">
                {rows.map(row => {
                  const decl = row.declaration;
                  const ps = row.payslip;
                  const hourly = isHourly(row);
                  const isFailed = ps?.status === 'failed';

                  return (
                    <tr key={row.employee.id}>
                      <td className="fw-bold">
                        {row.employee.name || row.employee.employee_number}
                        {row.employee.employee_number && (
                          <span className="text-muted ms-2 fs-8">#{row.employee.employee_number}</span>
                        )}
                      </td>

                      <td>
                        <span className={`badge ${hourly ? 'badge-light-info' : 'badge-light-primary'} fs-8`}>
                          {hourly ? lang.labels.salaryMode_hourly : lang.labels.salaryMode_monthly}
                        </span>
                      </td>

                      {showAsDays && (
                        <td className="text-muted" style={{ fontStyle: 'italic' }}>
                          {decl?.as_days ?? row.employee.periodDays?.full ?? 30}
                        </td>
                      )}

                      {showAbsenceDays && (
                        <td className="text-muted" style={{ fontStyle: 'italic' }}>
                          {decl?.absence_days ?? 0}
                        </td>
                      )}

                      <td>
                        {hourly ? (
                          <span>{decl?.quantities?.find(q => q.code === 'BASE_SALARY_HOURLY')?.quantity ?? 0}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      {hasPayslips && (
                        <td>{isFailed ? '-' : ps ? formatMoney(payslipGross(ps)) : '-'}</td>
                      )}

                      {hasPayslips && (
                        <td>{isFailed ? '-' : ps ? formatMoney(payslipNet(ps)) : '-'}</td>
                      )}

                      <td>
                        {isFailed ? (
                          <span className="badge badge-light-danger fs-8">{lang.labels.payslipFailed}</span>
                        ) : decl?.status === 'validated' ? (
                          <span className="badge badge-light-success fs-8">{lang.labels.declarationValidated}</span>
                        ) : decl ? (
                          <span className="badge badge-light-warning fs-8">{lang.labels.declarationDraft}</span>
                        ) : (
                          <span className="text-muted fs-8">-</span>
                        )}
                      </td>

                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-1">
                          {editable && (
                            <Link
                              to={`/${langCode}/main/payroll/runs/view/${runId}/declaration/${row.employee.id}`}
                              className="btn btn-sm btn-icon btn-light-primary"
                              title={lang.labels.editDeclaration}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </Link>
                          )}
                          {ps && !isFailed && onViewPayslip && (
                            <button
                              className="btn btn-sm btn-icon btn-light-primary"
                              title={lang.labels.view}
                              onClick={() => onViewPayslip(ps)}
                              disabled={pdfLoading === ps.id}
                            >
                              {pdfLoading === ps.id
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <i className="bi bi-eye"></i>
                              }
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Excluded employees */}
        {excluded.length > 0 && (
          <div className="mt-5">
            <h6 className="fw-bolder text-muted">
              <i className="bi bi-person-x-fill text-muted me-2"></i>
              {lang.labels.excludedEmployees} ({excluded.length})
            </h6>
            {excluded.map((e, i) => (
              <div key={i} className="d-flex align-items-center py-2 border-bottom">
                <span className="fw-bold me-3">{e.name || e.employee_number}</span>
                <span className="text-muted">{exclusionLabel(lang, e.reason)}</span>
                {e.reason_detail && <span className="text-muted ms-2">({e.reason_detail})</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyDeclarationTable;
